/**
 * G1: a newer version was deployed. A small icon offers the reload — the page never reloads by itself. In the
 * signed-in rail (above the version line), in the phone header, and as a corner icon on pages without the shell
 * (public, guest and hotel pages). Renders nothing until the tab is out of date.
 */
import { RefreshCw } from 'lucide-react';
import { cn } from '@/lib/cn';
import { reloadForUpdate, useUpdateAvailable } from '@/lib/updateAvailable';
import { InfoTip } from '@/components/ui/InfoTip';

const NAME = 'Update available, click to reload';

export function UpdateAvailable({ variant, compact = false, placement = 'top' }: {
  variant: 'rail' | 'header' | 'corner';
  /** rail only: the collapsed (icon-only) rail */
  compact?: boolean;
  /** corner only */
  placement?: 'top' | 'bottom';
}) {
  const stale = useUpdateAvailable();
  if (!stale) return null;

  const mark = (
    <span className="relative inline-flex shrink-0">
      <RefreshCw className="size-4" aria-hidden />
      <span aria-hidden className="absolute -right-1 -top-1 size-2 rounded-full bg-brand ring-2 ring-shell" />
    </span>
  );

  if (variant === 'rail') {
    const button = (
      <button
        type="button"
        data-testid="update-available"
        aria-label={NAME}
        title={NAME}
        onClick={reloadForUpdate}
        className={cn(
          'flex min-h-11 min-w-11 items-center gap-2.5 rounded-control px-2.5 py-2 text-sm font-semibold text-ink hover:bg-shell-hairline/50 print:hidden',
          compact ? 'justify-center' : 'min-w-0 flex-1',
        )}
      >
        {mark}
        {!compact && <span className="truncate">Update available</span>}
      </button>
    );
    if (compact) return button;
    return (
      <div className="flex items-center gap-1 pr-2 print:hidden">
        {button}
        <InfoTip title="Update available" side="top" align="end">
          A newer version is ready. Nothing reloads by itself — click when you're ready; your saved work stays.
        </InfoTip>
      </div>
    );
  }

  if (variant === 'header') {
    // the header's own buttons are 36 px; the 44 px target is an absolutely placed area behind the button (as InfoTip's),
    // inside the header's 8 px gaps, so the crowded phone header gives up no more room than a 36 px button
    return (
      <button
        type="button"
        data-testid="update-available"
        aria-label={NAME}
        title={NAME}
        onClick={reloadForUpdate}
        className="relative inline-flex size-9 shrink-0 items-center justify-center rounded-control text-ink hover:bg-surface-2 print:hidden"
      >
        <span aria-hidden data-hit-target className="absolute -inset-1" />
        {mark}
      </button>
    );
  }

  return (
    <button
      type="button"
      data-testid="update-available"
      aria-label={NAME}
      title={NAME}
      onClick={reloadForUpdate}
      className={cn(
        'inline-flex size-11 items-center justify-center rounded-control text-ink print:hidden',
        'right-[max(0.75rem,env(safe-area-inset-right))] z-toast bg-surface shadow-lg ring-1 ring-line-strong hover:bg-surface-2',
        placement === 'bottom' ? 'fixed bottom-[max(0.75rem,env(safe-area-inset-bottom))]' : 'absolute top-[max(0.75rem,env(safe-area-inset-top))]',
      )}
    >
      {mark}
    </button>
  );
}
